import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Smartphone, MapPin, Check, Loader2, LogOut } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Logo } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { UFS, digits, maskPhone } from "@/lib/brdocs";

function useCooldown() {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!left) return;
    const t = setTimeout(() => setLeft((l) => l - 1), 1000);
    return () => clearTimeout(t);
  }, [left]);
  return [left, () => setLeft(60)];
}

function OtpStep({ channel, onDone, children, canSend = true, payload }) {
  const [sentTo, setSentTo] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [left, start] = useCooldown();

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/onboarding/${channel}/send`, payload?.());
      if (data.verified) return onDone();
      setSentTo(data.sent_to);
      start();
      toast.success("Código enviado");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      await api.post(`/onboarding/${channel}/verify`, { code });
      toast.success(channel === "email" ? "E-mail confirmado" : "Celular confirmado");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); setCode(""); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      {children}
      {sentTo && <p className="text-xs text-muted-foreground" data-testid={`otp-${channel}-sent-to`}>Código enviado para <b className="text-white">{sentTo}</b></p>}
      {sentTo && (
        <Input value={code} onChange={(e) => setCode(digits(e.target.value).slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" placeholder="000000"
          className="h-14 rounded-xl bg-white/5 text-center text-2xl font-extrabold tracking-[0.5em]" data-testid={`otp-${channel}-code-input`} />
      )}
      <div className="flex gap-2">
        {sentTo && <Button onClick={verify} disabled={busy || code.length !== 6} className="h-11 flex-1 rounded-full font-bold" data-testid={`otp-${channel}-verify-button`}>Confirmar código</Button>}
        <Button variant={sentTo ? "secondary" : "default"} onClick={send} disabled={busy || left > 0 || !canSend} className={`h-11 rounded-full font-bold ${sentTo ? "" : "flex-1"}`} data-testid={`otp-${channel}-send-button`}>
          {busy && !sentTo ? <Loader2 size={16} className="animate-spin" /> : left > 0 ? `Reenviar em ${left}s` : sentTo ? "Reenviar" : "Enviar código"}
        </Button>
      </div>
    </div>
  );
}

function StepCard({ n, icon: Icon, title, done, active, children }) {
  return (
    <section className={`rounded-3xl border p-5 transition-colors ${active ? "border-[hsl(var(--gold))]/50 bg-card" : "border-white/5 bg-card/40"}`} data-testid={`onboarding-step-${n}`}>
      <div className="flex items-center gap-3">
        <span className={`grid h-9 w-9 place-items-center rounded-full ${done ? "bg-emerald-500 text-black" : active ? "bg-[hsl(var(--gold))] text-[#0B0F17]" : "bg-white/10 text-muted-foreground"}`}>{done ? <Check size={17} /> : <Icon size={17} />}</span>
        <p className={`font-bold ${active || done ? "" : "text-muted-foreground"}`}>{title}</p>
      </div>
      {active && <div className="mt-4">{children}</div>}
    </section>
  );
}

function PlaceStep({ user, onDone }) {
  const [f, setF] = useState({ city: "", state: "", birth_date: "" });
  const [busy, setBusy] = useState(false);
  const needBirth = !user.birth_date;
  const save = async (e) => {
    e.preventDefault();
    if (!f.state) return toast.error("Escolha o estado");
    setBusy(true);
    try { onDone((await api.post("/onboarding/complete", f)).data); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="space-y-3">
      <div className="grid grid-cols-[1fr_96px] gap-3">
        <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Cidade</Label>
          <Input required value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="h-11 rounded-xl bg-white/5" data-testid="onboarding-city-input" /></div>
        <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">UF</Label>
          <Select value={f.state} onValueChange={(v) => setF({ ...f, state: v })}>
            <SelectTrigger className="h-11 rounded-xl bg-white/5" data-testid="onboarding-state-select"><SelectValue placeholder="UF" /></SelectTrigger>
            <SelectContent>{UFS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
          </Select></div>
      </div>
      {needBirth && <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Data de nascimento</Label>
        <Input required type="date" value={f.birth_date} onChange={(e) => setF({ ...f, birth_date: e.target.value })} className="h-11 rounded-xl bg-white/5" data-testid="onboarding-birthdate-input" /></div>}
      <Button type="submit" disabled={busy} className="h-11 w-full rounded-full font-bold" data-testid="onboarding-complete-button">{busy ? "Salvando…" : "Concluir e entrar"}</Button>
    </form>
  );
}

export default function Onboarding() {
  const { user, setUser, logout } = useAuth();
  const [emailOk, setEmailOk] = useState(!!user.email_verified);
  const [phoneOk, setPhoneOk] = useState(!!user.phone_verified);
  const [phone, setPhone] = useState("");
  const step = !emailOk ? 1 : !phoneOk ? 2 : 3;

  return (
    <div className="min-h-screen px-5 py-10" data-testid="onboarding-page">
      <div className="mx-auto max-w-md space-y-4">
        <div className="flex items-center justify-between"><Logo />
          <button onClick={logout} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-white" data-testid="onboarding-logout-button"><LogOut size={14} /> Sair</button></div>
        <div className="pb-2 pt-6">
          <h1 className="text-3xl font-extrabold tracking-tight">Proteja sua conta</h1>
          <p className="mt-1 text-sm text-muted-foreground">Três passos rápidos para manter a comunidade segura.</p>
        </div>
        <StepCard n={1} icon={Mail} title="Confirmar e-mail" done={emailOk} active={step === 1}>
          <OtpStep channel="email" onDone={() => setEmailOk(true)}><p className="text-sm text-muted-foreground">Vamos enviar um código de 6 dígitos para <b className="text-white">{user.email}</b>.</p></OtpStep>
        </StepCard>
        <StepCard n={2} icon={Smartphone} title="Confirmar celular" done={phoneOk} active={step === 2}>
          <OtpStep channel="phone" onDone={() => setPhoneOk(true)} canSend={digits(phone).length >= 10} payload={() => ({ phone })}>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Número com DDD</Label>
              <Input value={phone} onChange={(e) => setPhone(maskPhone(e.target.value))} inputMode="tel" placeholder="(11) 91234-5678" className="h-11 rounded-xl bg-white/5" data-testid="onboarding-phone-input" /></div>
          </OtpStep>
        </StepCard>
        <StepCard n={3} icon={MapPin} title="Cidade e estado" done={false} active={step === 3}>
          <PlaceStep user={user} onDone={(u) => { setUser(u); toast.success("Conta verificada!"); }} />
        </StepCard>
      </div>
    </div>
  );
}
